// Production-safe API configuration with smart LAN/cloud fallback
// Supports: LAN deployment (no internet), cloud API, and hybrid scenarios
// Kiosk-optimized: endpoint stickiness, flapping prevention, adaptive timeouts

import { CapacitorHttp } from '@capacitor/core';

export const LAN_SERVER_URL = 'http://192.168.20.50';
export const CLOUD_API_URL = 'https://api.fids.eximenon.com';

// Network resilience settings for kiosk stability
const LAN_HEALTH_TIMEOUT_BASE_MS = 2000; // Base timeout for LAN check
const LAN_HEALTH_TIMEOUT_MAX_MS = 5000;  // Max timeout after failures
const CONSECUTIVE_FAILURES_TO_SWITCH = 3; // Require 3 failures before switching endpoints
const ENDPOINT_CACHE_KEY = 'fids_last_good_endpoint';
const FAILURE_COUNT_KEY = 'fids_endpoint_failures';

// Mutable state for dynamic API URL (can change after LAN detection)
let _apiBaseUrl = null;
let _socketUrl = null;
let _isLanAvailable = null;
let _detectionPromise = null;
let _consecutiveFailures = 0;
let _currentTimeout = LAN_HEALTH_TIMEOUT_BASE_MS;

/**
 * Get cached endpoint preference (stickiness for kiosk stability)
 */
const getCachedEndpoint = () => {
  try {
    return localStorage.getItem(ENDPOINT_CACHE_KEY);
  } catch {
    return null;
  }
};

/**
 * Cache the current working endpoint
 */
const setCachedEndpoint = (endpoint) => {
  try {
    localStorage.setItem(ENDPOINT_CACHE_KEY, endpoint);
    localStorage.setItem(FAILURE_COUNT_KEY, '0');
    _consecutiveFailures = 0;
  } catch {
    // localStorage not available
  }
};

/**
 * Track endpoint failures for flapping prevention
 */
const recordEndpointFailure = () => {
  _consecutiveFailures++;
  // Adaptive timeout: increase after failures (up to max)
  _currentTimeout = Math.min(
    LAN_HEALTH_TIMEOUT_BASE_MS + (_consecutiveFailures * 500),
    LAN_HEALTH_TIMEOUT_MAX_MS
  );
  try {
    localStorage.setItem(FAILURE_COUNT_KEY, String(_consecutiveFailures));
  } catch {
    // localStorage not available
  }
  return _consecutiveFailures >= CONSECUTIVE_FAILURES_TO_SWITCH;
};

/**
 * Check if Capacitor native HTTP is available
 * Safe to call even if Capacitor isn't ready yet
 */
const isCapacitorHttpAvailable = () => {
  try {
    return typeof window !== 'undefined' &&
           window.Capacitor !== undefined &&
           typeof CapacitorHttp?.get === 'function';
  } catch {
    return false;
  }
};

/**
 * Check if LAN server is reachable with adaptive timeout
 * Uses CapacitorHttp (native) on Android to avoid mixed-content issues
 */
const checkLanAvailable = async () => {
  const healthUrl = `${LAN_SERVER_URL}/health`;
  console.log(`🔍 LAN health check: ${healthUrl} (timeout: ${_currentTimeout}ms)`);

  try {
    let isOk = false;

    // Use native HTTP on Capacitor to avoid mixed-content blocking
    if (isCapacitorHttpAvailable()) {
      console.log('🔍 Using CapacitorHttp (native) for LAN check');
      try {
        const response = await Promise.race([
          CapacitorHttp.get({
            url: healthUrl,
            connectTimeout: _currentTimeout,
            readTimeout: _currentTimeout,
          }),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Timeout')), _currentTimeout + 500)
          ),
        ]);
        isOk = response.status >= 200 && response.status < 300;
        console.log(`🔍 LAN health response (native): ${response.status} ${isOk ? 'OK' : 'FAIL'}`);
      } catch (nativeErr) {
        console.log(`🔍 LAN health check failed (native): ${nativeErr.message}`);
        isOk = false;
      }
    } else {
      // Web fallback - may have mixed-content issues but works in browser dev
      console.log('🔍 Using fetch (WebView) for LAN check');
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), _currentTimeout);

      try {
        const response = await fetch(healthUrl, {
          method: 'GET',
          signal: controller.signal,
          cache: 'no-store',
        });
        clearTimeout(timeoutId);
        isOk = response.ok;
        console.log(`🔍 LAN health response (fetch): ${response.status} ${isOk ? 'OK' : 'FAIL'}`);
      } catch (fetchErr) {
        clearTimeout(timeoutId);
        console.log(`🔍 LAN health check failed (fetch): ${fetchErr.name} - ${fetchErr.message}`);
        isOk = false;
      }
    }

    if (isOk) {
      // Reset failure count and timeout on success
      _consecutiveFailures = 0;
      _currentTimeout = LAN_HEALTH_TIMEOUT_BASE_MS;
    }

    return isOk;
  } catch (err) {
    // Catch-all for any unexpected errors
    console.log(`🔍 LAN health check failed: ${err.name} - ${err.message}`);
    return false;
  }
};

/**
 * Determine API base URL based on current context
 */
const getApiBaseUrl = () => {
  const hostname = window.location.hostname;

  // If already loaded from a private LAN IP (via Capacitor server.url)
  const isPrivateLan = /^(192\.168\.|10\.|172\.(1[6-9]|2\d|3[01])\.)/.test(hostname);
  const isCapacitorApp = window.Capacitor !== undefined;

  if (isPrivateLan) {
    // CapacitorHttp (native HTTP plugin) requires FULL URLs - relative paths don't work
    // Browser fetch() can use relative paths, but CapacitorHttp can't resolve them
    if (isCapacitorApp) {
      const fullLanUrl = `${window.location.protocol}//${hostname}`;
      console.log('🏢 Private LAN + Capacitor detected (', hostname, ') - using full LAN URL:', fullLanUrl);
      return fullLanUrl;  // Return full URL for CapacitorHttp compatibility
    }
    console.log('🏢 Private LAN detected (', hostname, ') - using relative API paths via NGINX proxy');
    return '';  // relative path: fetch('/api/...') goes through NGINX proxy (web only)
  }

  // Check if running in Electron (file:// protocol, but NOT Capacitor)
  const isElectronApp = !isCapacitorApp && (
    window.location.protocol === 'file:' ||
    !window.location.hostname ||
    window.location.hostname === ''
  );

  if (isElectronApp) {
    console.log('🔧 Electron detected - using cloud API');
    return process.env.REACT_APP_BACKEND_URL || CLOUD_API_URL;
  }

  // Check if running in Capacitor (Android/iOS app loading from local assets)
  // LAN detection: Check if LAN server is reachable, fall back to cloud if not
  if (isCapacitorApp && (hostname === 'localhost' || hostname === '127.0.0.1' || !hostname)) {
    console.log('📱 Capacitor detected - triggering async LAN detection');
    return null; // Trigger async LAN detection (checks LAN first, falls back to cloud)
  }

  // Development localhost (web browser, not Capacitor)
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    const envUrl = process.env.REACT_APP_BACKEND_URL;
    if (envUrl && (envUrl.includes('localhost') || envUrl.includes('127.0.0.1'))) {
      console.log('🔧 Using local backend:', envUrl);
      return envUrl;
    }
    return envUrl || CLOUD_API_URL;
  }

  // Public domain
  if (hostname === 'fids.eximenon.com' || hostname.includes('eximenon.com')) {
    return CLOUD_API_URL;
  }

  // Default: will be determined by LAN detection
  return null; // null means "needs async detection"
};

/**
 * Initialize API URL - runs sync check first, then async LAN detection if needed
 */
const initApiUrl = () => {
  const syncResult = getApiBaseUrl();

  if (syncResult !== null) {
    // Sync detection was conclusive
    _apiBaseUrl = syncResult;
    _socketUrl = syncResult || undefined;
    _isLanAvailable = syncResult === '';
    return;
  }

  // Need async LAN detection - start with cloud as safe default
  _apiBaseUrl = CLOUD_API_URL;
  _socketUrl = CLOUD_API_URL;
  _isLanAvailable = false;

  // Trigger async detection
  _detectionPromise = detectAndSetEndpoint();
};

/**
 * Async LAN detection - checks if LAN server is reachable
 * If LAN fails, immediately falls back to cloud (no stickiness when not on LAN)
 * Stickiness is only applied when app is deployed to LAN (handled sync in getApiBaseUrl)
 */
const detectAndSetEndpoint = async () => {
  const cachedEndpoint = getCachedEndpoint();
  console.log('🔍 Checking LAN availability...', cachedEndpoint ? `(cached: ${cachedEndpoint})` : '');

  const lanAvailable = await checkLanAvailable();

  if (lanAvailable) {
    console.log('✅ LAN server reachable - using LAN mode');
    _apiBaseUrl = LAN_SERVER_URL;
    _socketUrl = LAN_SERVER_URL;
    _isLanAvailable = true;
    setCachedEndpoint('lan');
  } else {
    // LAN not reachable - immediately fall back to cloud
    // No stickiness here: if we can't reach LAN, use cloud right away
    // This prevents app from being stuck when device is not on LAN network
    console.log('☁️ LAN not reachable - using cloud API');
    _apiBaseUrl = CLOUD_API_URL;
    _socketUrl = CLOUD_API_URL;
    _isLanAvailable = false;
    setCachedEndpoint('cloud');
    // Reset failure count since we're switching
    _consecutiveFailures = 0;
    _currentTimeout = LAN_HEALTH_TIMEOUT_BASE_MS;
  }

  return { apiBaseUrl: _apiBaseUrl, socketUrl: _socketUrl, isLanAvailable: _isLanAvailable };
};

// Initialize on module load
initApiUrl();

// Export getters (allows dynamic updates after async detection)
export const getApiBaseUrl_dynamic = () => _apiBaseUrl;
export const getSocketUrl_dynamic = () => _socketUrl;
export const isLanAvailable = () => _isLanAvailable;

// Promise that resolves when detection is complete (for components that need to wait)
export const apiConfigReady = _detectionPromise || Promise.resolve({
  apiBaseUrl: _apiBaseUrl,
  socketUrl: _socketUrl,
  isLanAvailable: _isLanAvailable
});

// Static exports for backward compatibility (uses initial sync value)
export const API_BASE_URL = _apiBaseUrl;

// Socket.IO URL - follows same logic as API base URL
export const SOCKET_URL = _socketUrl;

// API Key for authenticated requests
export const API_KEY = process.env.REACT_APP_API_KEY || '466504849eb593053e7a5ee4e707a6577fb050717e3fa4e9cc8696ad9ed1c2b1';

// Platform detection utilities
// IMPORTANT: Use getIsAndroid() function for runtime checks - static exports may be
// evaluated before Capacitor injects window.Capacitor, causing false negatives
export const isCapacitor = window.Capacitor !== undefined;
export const isElectron = window.location.protocol === 'file:' || !window.location.hostname;
export const isAndroid = isCapacitor && window.Capacitor?.getPlatform?.() === 'android';
export const isIOS = isCapacitor && window.Capacitor?.getPlatform?.() === 'ios';
export const isWeb = !isCapacitor && !isElectron;
export const isPrivateLan = /^(192\.168\.|10\.|172\.(1[6-9]|2\d|3[01])\.)/.test(window.location.hostname);

/**
 * Dynamic platform detection - use these functions for runtime checks
 * Static exports above may fail if evaluated before Capacitor initializes
 */
export const getIsCapacitor = () => window.Capacitor !== undefined;
export const getIsAndroid = () => getIsCapacitor() && window.Capacitor?.getPlatform?.() === 'android';
export const getIsIOS = () => getIsCapacitor() && window.Capacitor?.getPlatform?.() === 'ios';

// Log initial state (async detection may update these values later)
console.log('API Config initialized:', {
  platform: isCapacitor ? (window.Capacitor?.getPlatform?.() || 'capacitor-unknown') : (isElectron ? 'electron' : 'web'),
  privateLan: isPrivateLan,
  envVar: process.env.REACT_APP_BACKEND_URL,
  hostname: window.location.hostname,
  initialUrl: _apiBaseUrl || '(relative - same origin)',
  asyncDetection: _detectionPromise ? 'pending' : 'not needed'
});

/**
 * Build asset URL (for images, videos, etc.)
 * Uses the same base URL logic as API calls for consistency.
 * @param {string} path - Asset path (e.g., '/uploads/image.jpg' or 'uploads/image.jpg')
 * @returns {string} Full URL or relative path depending on context
 */
export const buildAssetUrl = (path) => {
  const baseUrl = getApiBaseUrl_dynamic();

  // Normalize path - ensure it starts with /
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;

  // If baseUrl is empty (same-origin via NGINX proxy), use relative path
  if (baseUrl === '' || baseUrl === null || baseUrl === undefined) {
    return normalizedPath;
  }

  // Otherwise, prefix with base URL (remove trailing slash if present)
  const base = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  return `${base}${normalizedPath}`;
};

/**
 * Global unhandled rejection handler
 * Logs errors without crashing the UI. Reports to console and optionally to heartbeat metadata.
 */
const initGlobalErrorHandlers = () => {
  // Track recent unhandled rejections for heartbeat reporting
  window._unhandledRejections = window._unhandledRejections || [];

  window.addEventListener('unhandledrejection', (event) => {
    const error = event.reason;
    const errorInfo = {
      message: error?.message || String(error),
      stack: error?.stack?.substring(0, 500) || null,
      timestamp: new Date().toISOString(),
      url: window.location.href
    };

    // Check if this is a network error (Axios)
    const isNetworkError = error?.message?.includes('Network Error') ||
                           error?.code === 'ERR_NETWORK' ||
                           error?.name === 'AxiosError';

    if (isNetworkError) {
      console.warn('🌐 Network error (handled gracefully):', errorInfo.message);
      // Don't spam console with full stack for common network errors
    } else {
      console.error('⚠️ Unhandled Promise Rejection:', errorInfo);
    }

    // Store for heartbeat reporting (keep last 5)
    window._unhandledRejections.push(errorInfo);
    if (window._unhandledRejections.length > 5) {
      window._unhandledRejections.shift();
    }

    // Prevent the default browser error reporting (which can crash the UI)
    event.preventDefault();
  });

  // Also catch synchronous errors
  window.addEventListener('error', (event) => {
    console.error('⚠️ Uncaught Error:', {
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno
    });
    // Don't prevent default for sync errors - let React error boundary handle them
  });

  console.log('🛡️ Global error handlers initialized');
};

// Initialize global error handlers
initGlobalErrorHandlers();
